import React, { useState } from 'react';

interface CalendarEvent {
  id: number;
  title: string;
  date: Date;
  type: 'meeting' | 'event' | 'biblestudy' | 'project';
}

interface CalendarWidgetProps {
  events: CalendarEvent[];
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({ events }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isToday = (day: number, month: number, year: number) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const getEventLabel = (type: string) => {
    switch (type) {
      case 'meeting': return 'Reunião';
      case 'event': return 'Evento';
      case 'biblestudy': return 'Estudo Bíblico';
      case 'project': return 'Entrega de Projeto';
      default: return 'Outro';
    }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthName = currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayEvents = events.filter(e => 
      e.date.getDate() === day && 
      e.date.getMonth() === month && 
      e.date.getFullYear() === year
    );

    days.push(
      <div key={day} className={`calendar-day ${isToday(day, month, year) ? 'today' : ''}`}>
        <span className="day-number">{day}</span>
        <div className="day-events">
          {dayEvents.map((e, idx) => (
            <div 
              key={idx} 
              className={`event-dot ${e.type}`} 
              title={`${e.title} (${getEventLabel(e.type)})`}
              onClick={() => setSelectedEvent(e)}
            >
              {e.title}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card calendar-widget">
      <div className="calendar-header">
        <button onClick={handlePrevMonth} className="btn btn-secondary">&lt;</button>
        <h3>{monthName}</h3>
        <button onClick={handleNextMonth} className="btn btn-secondary">&gt;</button>
      </div>
      <div className="calendar-grid">
        <div className="weekday">Dom</div>
        <div className="weekday">Seg</div>
        <div className="weekday">Ter</div>
        <div className="weekday">Qua</div>
        <div className="weekday">Qui</div>
        <div className="weekday">Sex</div>
        <div className="weekday">Sáb</div>
        {days}
      </div>

      {/* Modal de Detalhes do Evento */}
      {selectedEvent && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', maxWidth: '90%', padding: '30px', position: 'relative' }}>
            <button 
              onClick={() => setSelectedEvent(null)} 
              style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
            >
              &times;
            </button>
            
            <h3 style={{ marginBottom: '10px', color: 'var(--primary-blue)' }}>{selectedEvent.title}</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <span className={`badge badge-${selectedEvent.type === 'meeting' ? 'info' : selectedEvent.type === 'event' ? 'warning' : selectedEvent.type === 'biblestudy' ? 'success' : 'secondary'}`}>
                {getEventLabel(selectedEvent.type)}
              </span>
            </div>

            <p><strong>Data:</strong> {selectedEvent.date.toLocaleDateString()}</p>
            {selectedEvent.type !== 'project' && (
              <p><strong>Horário:</strong> {selectedEvent.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
            )}
            
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button className="btn btn-secondary" onClick={() => setSelectedEvent(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .calendar-widget { padding: 20px; }
        .calendar-header { display: flex; justify-content: space-between; alignItems: center; margin-bottom: 20px; text-transform: capitalize; }
        .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; }
        .weekday { text-align: center; font-weight: bold; color: var(--text-secondary); padding: 10px 0; }
        .calendar-day { 
          border: 1px solid var(--border-color); 
          border-radius: 4px; 
          min-height: 100px; 
          padding: 5px; 
          background: white;
          transition: background-color 0.2s;
        }
        .calendar-day.today {
          background-color: #f0f9ff;
          border-color: var(--primary-blue);
          box-shadow: inset 0 0 0 1px var(--primary-blue);
        }
        .calendar-day.empty { background: transparent; border: none; box-shadow: none; }
        .day-number { font-weight: bold; font-size: 0.9rem; margin-bottom: 5px; display: block; }
        .day-events { display: flex; flexDirection: column; gap: 2px; }
        .event-dot { 
          font-size: 0.75rem; 
          padding: 2px 4px; 
          border-radius: 4px; 
          white-space: nowrap; 
          overflow: hidden; 
          text-overflow: ellipsis; 
          color: white;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .event-dot:hover { opacity: 0.8; }
        .event-dot.meeting { background-color: var(--primary-blue); }
        .event-dot.event { background-color: var(--secondary-orange); }
        .event-dot.biblestudy { background-color: var(--success); }
        .event-dot.project { background-color: #9333ea; } /* Roxo para projetos */
      `}</style>
    </div>
  );
};

export default CalendarWidget;
